import { notifyError } from "@/lib/notify-error";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useConvex, useMutation } from "convex/react";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import {
  Check,
  Download,
  FileText,
  Loader2,
  Maximize2,
  MessageSquareMore,
  PencilLine,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { DeleteConfirmationDialog } from "@/components/delete-confirmation-dialog";
import { DocumentTypeLabel } from "@/components/document-type-label";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import {
  LinkedRevisionJobsButton,
  type LinkedRevisionJob,
} from "@/components/linked-revision-jobs";
import { PdfModalPipViewport } from "@/components/pdf-modal-pip";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  buildDocumentDownloadFilename,
  downloadUrlAsFile,
} from "@/lib/document-download";
import { useRevisionSheetLabels } from "@/features/sheet-index/use-revision-sheets";
import { cn, type DocumentTypeLabelValue } from "@/lib/utils";
import type { GlobalProcessingState } from "@/lib/processing-states";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PILOT_LINKED_DELETE_REASON =
  "Linked to existing SpecPilot, RevPilot, or TakeoffPilot items";
const JOB_ATTACHED_DELETE_REASON = "Can't delete because it is linked to a job.";

function formatDeleteBlockedReason(reason?: string | null): string | null {
  if (!reason) {
    return null;
  }

  return reason === PILOT_LINKED_DELETE_REASON ? JOB_ATTACHED_DELETE_REASON : reason;
}

interface RevisionRecord {
  _id: Id<"projectDocumentRevisions">;
  revisionNumber: number;
  revisionName: string;
  originalFilename: string;
  mimeType?: string;
  effectiveDocumentType?: DocumentTypeLabelValue;
  pageDocumentTypes?: DocumentTypeLabelValue[];
  classificationStatus?: "pending" | "processing" | "completed" | "failed";
  pageIndexingStatus?: GlobalProcessingState;
  classificationError?: string;
  indexingStatus?: "pending" | "processing" | "completed" | "failed";
  linkedJobs?: LinkedRevisionJob[];
}

interface ProjectDocumentViewerProps {
  documentId: Id<"projectDocuments">;
  documentName: string;
  revisions: RevisionRecord[];
  onViewFull?: (revisionId?: Id<"projectDocumentRevisions">) => void;
  onOpenRevCollab?: (revisionId: Id<"projectDocumentRevisions">) => void;
  onDeletedDocument?: () => void;
  showViewFullButton?: boolean;
  showOpenRevCollabButton?: boolean;
  initialRevisionId?: Id<"projectDocumentRevisions"> | string | null;
  /** Page to open the initial revision at, e.g. a sheet picked from the sheet index. */
  initialPage?: number;
  previewOnly?: boolean;
  className?: string;
  previewContainerClassName?: string;
  previewClassName?: string;
}

interface DocumentDeleteActionsArgs {
  documentId: Id<"projectDocuments">;
  selectedRevision: RevisionRecord | null;
  canDeleteRevision: boolean;
  revisionDeleteBlockedReason: string | null;
  canDeleteDocument: boolean;
  documentDeleteBlockedReason: string | null;
  onDeletedDocument?: () => void;
}

function useDocumentDeleteActions({
  documentId,
  selectedRevision,
  canDeleteRevision,
  revisionDeleteBlockedReason,
  canDeleteDocument,
  documentDeleteBlockedReason,
  onDeletedDocument,
}: DocumentDeleteActionsArgs) {
  const removeRevision = useMutation(api.projectDocuments.removeRevision);
  const removeDocument = useMutation(api.projectDocuments.removeDocument);
  const [isDeletingRevision, setIsDeletingRevision] = useState(false);
  const [isDeletingDocument, setIsDeletingDocument] = useState(false);

  const handleDeleteRevision = useCallback(async () => {
    if (!selectedRevision || !canDeleteRevision) {
      if (revisionDeleteBlockedReason) {
        toast.error(revisionDeleteBlockedReason);
      }
      return;
    }

    setIsDeletingRevision(true);
    try {
      await removeRevision({ revisionId: selectedRevision._id });
      toast.success("Revision deleted");
    } catch (error) {
      console.error(error);
      notifyError(error, error instanceof Error ? error.message : "Failed to delete revision");
    } finally {
      setIsDeletingRevision(false);
    }
  }, [
    selectedRevision,
    canDeleteRevision,
    revisionDeleteBlockedReason,
    removeRevision,
  ]);

  const handleDeleteDocument = useCallback(async () => {
    if (!canDeleteDocument) {
      if (documentDeleteBlockedReason) {
        toast.error(documentDeleteBlockedReason);
      }
      return;
    }

    setIsDeletingDocument(true);
    try {
      await removeDocument({ documentId });
      toast.success("Document deleted");
      onDeletedDocument?.();
    } catch (error) {
      console.error(error);
      notifyError(error, error instanceof Error ? error.message : "Failed to delete document");
    } finally {
      setIsDeletingDocument(false);
    }
  }, [
    canDeleteDocument,
    documentDeleteBlockedReason,
    removeDocument,
    documentId,
    onDeletedDocument,
  ]);

  return {
    isDeletingRevision,
    isDeletingDocument,
    handleDeleteRevision,
    handleDeleteDocument,
  };
}

function isPdfRevision(revision: RevisionRecord | null): boolean {
  if (!revision) return false;
  const mime = revision.mimeType?.toLowerCase().trim();
  if (mime === "application/pdf") return true;
  return revision.originalFilename.toLowerCase().endsWith(".pdf");
}

function isImageRevision(revision: RevisionRecord | null): boolean {
  if (!revision) return false;
  const mime = revision.mimeType?.toLowerCase().trim();
  if (mime?.startsWith("image/")) return true;
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(revision.originalFilename);
}

function isRevCollabSupported(revision: RevisionRecord | null): boolean {
  return isPdfRevision(revision) || isImageRevision(revision);
}

interface DocumentActionButtonsProps {
  selectedRevision: RevisionRecord | null;
  showViewFullButton: boolean;
  onViewFull?: (revisionId?: Id<"projectDocumentRevisions">) => void;
  showOpenRevCollabButton: boolean;
  onOpenRevCollab?: (revisionId: Id<"projectDocumentRevisions">) => void;
  isUploading: boolean;
  isDeletingRevision: boolean;
  isDeletingDocument: boolean;
  isCheckingDeleteEligibility: boolean;
  canDeleteDocument: boolean;
  documentDeleteBlockedReason: string | null;
  onDeleteDocument: () => void;
  isDownloading: boolean;
  onDownload: () => void;
  canDownload: boolean;
}

function DocumentActionButtons({
  selectedRevision,
  showViewFullButton,
  onViewFull,
  showOpenRevCollabButton,
  onOpenRevCollab,
  isUploading,
  isDeletingRevision,
  isDeletingDocument,
  isCheckingDeleteEligibility,
  canDeleteDocument,
  documentDeleteBlockedReason,
  onDeleteDocument,
  isDownloading,
  onDownload,
  canDownload,
}: DocumentActionButtonsProps) {
  const documentDisabledReason = isUploading
    ? "Please wait for the revision upload to finish before deleting."
    : isCheckingDeleteEligibility
      ? "Checking whether this document can be deleted..."
      : !canDeleteDocument
        ? documentDeleteBlockedReason
        : null;

  return (
    <>
      {showViewFullButton && onViewFull ? (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onViewFull(selectedRevision?._id)}
        >
          <Maximize2 className="size-4" />
          View Full
        </Button>
      ) : null}

      <Button
        variant="outline"
        size="sm"
        onClick={onDownload}
        disabled={!canDownload || isDownloading}
      >
        {isDownloading ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
        Download
      </Button>

      {showOpenRevCollabButton &&
      onOpenRevCollab &&
      selectedRevision &&
      isRevCollabSupported(selectedRevision) ? (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onOpenRevCollab(selectedRevision._id)}
        >
          <MessageSquareMore className="size-4" />
          Open in CollabPilot
        </Button>
      ) : null}

      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">
            <DeleteConfirmationDialog
              title="Delete document?"
              description="This permanently deletes the document and all revisions. This cannot be undone."
              onConfirm={onDeleteDocument}
              disabled={
                isUploading ||
                isDeletingDocument ||
                isDeletingRevision ||
                Boolean(documentDisabledReason)
              }
              confirmLabel={isDeletingDocument ? "Deleting..." : "Delete Document"}
            >
              <Button
                variant="destructive"
                size="sm"
                disabled={
                  isUploading ||
                  isDeletingDocument ||
                  isDeletingRevision ||
                  Boolean(documentDisabledReason)
                }
              >
                <Trash2 className="size-4" />
                Delete Document
              </Button>
            </DeleteConfirmationDialog>
          </span>
        </TooltipTrigger>
        {documentDisabledReason ? (
          <TooltipContent>
            <p>{documentDisabledReason}</p>
          </TooltipContent>
        ) : null}
      </Tooltip>
    </>
  );
}

interface RevisionDeleteButtonProps {
  selectedRevision: RevisionRecord | null;
  isUploading: boolean;
  isDeletingRevision: boolean;
  isDeletingDocument: boolean;
  disabledReason: string | null;
  onDeleteRevision: () => void;
}

function RevisionDeleteButton({
  selectedRevision,
  isUploading,
  isDeletingRevision,
  isDeletingDocument,
  disabledReason,
  onDeleteRevision,
}: RevisionDeleteButtonProps) {
  if (!selectedRevision) {
    return null;
  }

  const isDisabled =
    isUploading || isDeletingRevision || isDeletingDocument || Boolean(disabledReason);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex shrink-0">
          <DeleteConfirmationDialog
            title="Delete revision?"
            description="This permanently deletes the selected revision and its derived data. This cannot be undone."
            onConfirm={onDeleteRevision}
            disabled={isDisabled}
            confirmLabel={isDeletingRevision ? "Deleting..." : "Delete Revision"}
          >
            <Button
              type="button"
              variant="outline"
              size="icon-sm"
              className="text-muted-foreground hover:text-destructive"
              disabled={isDisabled}
              aria-label={`Delete revision ${selectedRevision.revisionName}`}
            >
              {isDeletingRevision ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Trash2 className="size-4" />
              )}
            </Button>
          </DeleteConfirmationDialog>
        </span>
      </TooltipTrigger>
      {disabledReason ? (
        <TooltipContent>
          <p>{disabledReason}</p>
        </TooltipContent>
      ) : null}
    </Tooltip>
  );
}

interface RevisionDetailsPanelProps {
  selectedRevision: RevisionRecord | null;
  downloadFilename: string;
  revisionDraftName: string;
  isEditingRevisionName: boolean;
  isSavingRevisionName: boolean;
  onRevisionDraftNameChange: (value: string) => void;
  onStartRevisionRename: () => void;
  onCancelRevisionRename: () => void;
  onSaveRevisionName: () => void;
}

function RevisionDetailsPanel({
  selectedRevision,
  downloadFilename,
  revisionDraftName,
  isEditingRevisionName,
  isSavingRevisionName,
  onRevisionDraftNameChange,
  onStartRevisionRename,
  onCancelRevisionRename,
  onSaveRevisionName,
}: RevisionDetailsPanelProps) {
  const normalizedRevisionDraftName = revisionDraftName.trim();
  const canSaveRevisionName =
    Boolean(selectedRevision) &&
    normalizedRevisionDraftName.length > 0 &&
    normalizedRevisionDraftName !== selectedRevision?.revisionName;

  if (!selectedRevision) {
    return (
      <p className="text-sm text-muted-foreground">No revisions available for this document.</p>
    );
  }

  return (
    <>
      <div className="space-y-2">
        <div className="flex min-w-0 items-center gap-2">
          {isEditingRevisionName ? (
            <>
              <Input
                aria-label="Revision name"
                value={revisionDraftName}
                onChange={(event) => onRevisionDraftNameChange(event.currentTarget.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    onSaveRevisionName();
                  }
                  if (event.key === "Escape") {
                    event.preventDefault();
                    onCancelRevisionRename();
                  }
                }}
                className="h-8 min-w-0 flex-1 text-sm"
              />
              <IconButton
                label="Save revision name"
                icon={Check}
                size="xs"
                variant="outline"
                iconClassName="size-4"
                onClick={onSaveRevisionName}
                disabled={!canSaveRevisionName || isSavingRevisionName}
              />
              <IconButton
                label="Cancel revision rename"
                icon={X}
                size="xs"
                iconClassName="size-4"
                onClick={onCancelRevisionRename}
                disabled={isSavingRevisionName}
              />
            </>
          ) : (
            <>
              <p className="min-w-0 truncate text-sm font-medium">{selectedRevision.revisionName}</p>
              <LinkedRevisionJobsButton jobs={selectedRevision.linkedJobs} compact hideWhenEmpty />
              <IconButton
                label="Rename revision"
                icon={PencilLine}
                size="xs"
                iconClassName="size-4"
                onClick={onStartRevisionRename}
              />
            </>
          )}
        </div>
        <p className="break-words text-xs text-muted-foreground">
          Revision {selectedRevision.revisionNumber} · {downloadFilename}
        </p>
      </div>
      <div className="mt-3 flex flex-wrap gap-2 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          Type:
          <DocumentTypeLabel
            documentType={selectedRevision.effectiveDocumentType}
            pageDocumentTypes={selectedRevision.pageDocumentTypes}
            classificationStatus={selectedRevision.classificationStatus}
            pageIndexingStatus={selectedRevision.pageIndexingStatus}
            indexingStatus={selectedRevision.indexingStatus}
            classificationError={selectedRevision.classificationError}
          />
        </span>
      </div>
      {selectedRevision.classificationError ? (
        <p className="mt-2 text-xs text-destructive">{selectedRevision.classificationError}</p>
      ) : null}
    </>
  );
}

interface RevisionPreviewPanelProps {
  sortedRevisions: RevisionRecord[];
  selectedRevision: RevisionRecord | null;
  downloadUrl: string | null | undefined;
  downloadFilename: string;
  onDownload?: () => void;
  isDownloading?: boolean;
  initialPage?: number;
  pageLabels?: ReadonlyMap<number, string>;
  className?: string;
}

function RevisionPreviewPanel({
  sortedRevisions,
  selectedRevision,
  downloadUrl,
  downloadFilename,
  onDownload,
  isDownloading = false,
  initialPage,
  pageLabels,
  className,
}: RevisionPreviewPanelProps) {
  if (sortedRevisions.length === 0) {
    return (
      <div
        className={cn(
          "flex h-[480px] items-center justify-center px-6 text-center text-sm text-muted-foreground",
          className
        )}
      >
        Upload the first revision to preview this document.
      </div>
    );
  }

  if (downloadUrl === undefined) {
    return (
      <div
        className={cn(
          "flex h-[480px] items-center justify-center text-muted-foreground",
          className
        )}
      >
        <Loader2 className="size-5 animate-spin" />
      </div>
    );
  }

  if (!downloadUrl) {
    return (
      <div
        className={cn(
          "flex h-[480px] items-center justify-center px-6 text-center text-sm text-muted-foreground",
          className
        )}
      >
        Unable to load this revision preview.
      </div>
    );
  }

  if (isImageRevision(selectedRevision)) {
    return (
      <div
        className={cn(
          "relative flex h-[480px] items-center justify-center overflow-hidden rounded-lg bg-muted/20",
          className
        )}
      >
        {onDownload ? (
          <div className="absolute right-3 top-3 z-10">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="bg-background/95"
              onClick={onDownload}
              disabled={isDownloading}
            >
              {isDownloading ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
              Download
            </Button>
          </div>
        ) : null}
        <img
          src={downloadUrl}
          alt={downloadFilename}
          className="max-h-full max-w-full object-contain"
        />
      </div>
    );
  }

  if (isPdfRevision(selectedRevision)) {
    return (
      <PdfModalPipViewport
        sourcePdf={{
          fileUrl: downloadUrl,
          filename: downloadFilename,
        }}
        sourceId={String(selectedRevision?._id ?? "project-document-preview")}
        initialPage={initialPage}
        pageLabels={pageLabels}
        className={cn("h-[480px] w-full rounded-lg", className)}
        toolbarActions={
          onDownload ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onDownload}
              disabled={isDownloading}
            >
              {isDownloading ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
              Download
            </Button>
          ) : null
        }
      />
    );
  }

  return (
    <div
      className={cn(
        "flex h-[480px] flex-col items-center justify-center gap-4 px-6 text-center",
        className
      )}
    >
      <FileText className="size-10 text-muted-foreground" />
      <p className="text-sm text-muted-foreground">Preview is unavailable for this file type.</p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {onDownload ? (
          <Button variant="outline" size="sm" onClick={onDownload} disabled={isDownloading}>
            {isDownloading ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
            Download
          </Button>
        ) : null}
        <Button asChild variant="outline" size="sm">
          <a href={downloadUrl} target="_blank" rel="noreferrer">
            Open File
          </a>
        </Button>
      </div>
    </div>
  );
}

/**
 * Renders a project document revision viewer with revision selection and preview.
 *
 * @param props.documentId - The parent project document ID.
 * @param props.revisions - Available revisions for the document.
 * Each revision includes `_id`, `revisionNumber`, `revisionName`, `originalFilename`, and optional `mimeType`.
 * @param props.onViewFull - Optional callback used by the "View Full" action.
 * @param props.showViewFullButton - Optional toggle for showing the "View Full" button. Defaults to `false`.
 * @returns A React element for selecting and previewing project document revisions.
 */
export function ProjectDocumentViewer({
  documentId,
  documentName,
  revisions,
  onViewFull,
  onOpenRevCollab,
  onDeletedDocument,
  showViewFullButton = false,
  showOpenRevCollabButton = false,
  initialRevisionId,
  initialPage,
  previewOnly = false,
  className,
  previewContainerClassName,
  previewClassName,
}: ProjectDocumentViewerProps) {
  const convex = useConvex();
  const renameRevision = useMutation(api.projectDocuments.renameRevision);
  const sortedRevisions = useMemo(
    () =>
      revisions
        .slice()
        .sort((a, b) =>
          b.revisionNumber - a.revisionNumber || String(b._id).localeCompare(String(a._id))
        ),
    [revisions]
  );
  const [selectedRevisionId, setSelectedRevisionId] = useState<string>(
    String(initialRevisionId ?? sortedRevisions[0]?._id ?? "")
  );
  const [revisionDraftName, setRevisionDraftName] = useState(sortedRevisions[0]?.revisionName ?? "");
  const [isEditingRevisionName, setIsEditingRevisionName] = useState(false);
  const [isSavingRevisionName, setIsSavingRevisionName] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const selectedRevision = useMemo(
    () =>
      sortedRevisions.find((revision) => String(revision._id) === selectedRevisionId) ?? null,
    [sortedRevisions, selectedRevisionId]
  );

  const pageLabels = useRevisionSheetLabels(
    isPdfRevision(selectedRevision) && selectedRevision?.pageIndexingStatus
      ? selectedRevision._id
      : null
  );
  const previewInitialPage =
    selectedRevisionId === String(initialRevisionId ?? "") ? initialPage : undefined;

  const downloadFilename = useMemo(
    () =>
      buildDocumentDownloadFilename(
        documentName,
        selectedRevision?.originalFilename ?? "document.pdf",
        selectedRevision?.revisionNumber
      ),
    [documentName, selectedRevision?.originalFilename, selectedRevision?.revisionNumber]
  );

  useEffect(() => {
    setSelectedRevisionId((current) => {
      if (sortedRevisions.length === 0) return "";
      if (
        current &&
        sortedRevisions.some((revision) => String(revision._id) === current)
      ) {
        return current;
      }
      return String(sortedRevisions[0]?._id ?? "");
    });
  }, [documentId, sortedRevisions]);

  useEffect(() => {
    if (
      initialRevisionId &&
      sortedRevisions.some((revision) => String(revision._id) === String(initialRevisionId))
    ) {
      setSelectedRevisionId(String(initialRevisionId));
    }
  }, [initialRevisionId, sortedRevisions]);

  useEffect(() => {
    setRevisionDraftName(selectedRevision?.revisionName ?? "");
    setIsEditingRevisionName(false);
  }, [selectedRevision?._id, selectedRevision?.revisionName]);

  const { data: downloadUrl } = useQuery(
    convexQuery(
      api.projectDocuments.getRevisionDownloadUrl,
      selectedRevision
        ? { revisionId: selectedRevision._id }
        : "skip"
    )
  );

  const handleDownload = useCallback(async () => {
    if (!selectedRevision || isDownloading) {
      return;
    }

    setIsDownloading(true);
    try {
      const freshDownloadUrl = await convex.query(api.projectDocuments.getRevisionDownloadUrl, {
        revisionId: selectedRevision._id,
      });
      if (!freshDownloadUrl) {
        throw new Error("Download URL unavailable");
      }
      await downloadUrlAsFile(freshDownloadUrl, downloadFilename);
      toast.success("Download started");
    } catch (error) {
      console.error(error);
      notifyError(error, error instanceof Error ? error.message : "Failed to download document");
    } finally {
      setIsDownloading(false);
    }
  }, [convex, downloadFilename, isDownloading, selectedRevision]);

  const { data: deletionEligibility } = useQuery(
    convexQuery(api.projectDocuments.getDeletionEligibility, {
      documentId,
      revisionId: selectedRevision?._id,
    })
  );

  const isCheckingDeleteEligibility = deletionEligibility === undefined;
  const canDeleteRevision = selectedRevision
    ? !isCheckingDeleteEligibility && Boolean(deletionEligibility?.canDeleteSelectedRevision)
    : false;
  const revisionDeleteBlockedReason = selectedRevision
    ? formatDeleteBlockedReason(deletionEligibility?.selectedRevisionDeleteBlockedReason)
    : null;
  const canDeleteDocument =
    !isCheckingDeleteEligibility && Boolean(deletionEligibility?.canDeleteDocument);
  const documentDeleteBlockedReason = formatDeleteBlockedReason(
    deletionEligibility?.documentDeleteBlockedReason
  );

  const isOnlyRevision = sortedRevisions.length <= 1;
  const canDeleteRevisionInUi = canDeleteRevision && !isOnlyRevision;
  const revisionDeleteBlockedReasonInUi = isOnlyRevision
    ? "Cannot delete the only revision. Delete the document instead."
    : revisionDeleteBlockedReason;
  const revisionDisabledReason = isCheckingDeleteEligibility && selectedRevision
    ? "Checking whether this revision can be deleted..."
    : !canDeleteRevisionInUi
      ? (revisionDeleteBlockedReasonInUi ?? JOB_ATTACHED_DELETE_REASON)
      : null;

  const handleRenameRevision = async () => {
    const nextRevisionName = revisionDraftName.trim();
    if (!selectedRevision || !nextRevisionName || nextRevisionName === selectedRevision.revisionName) {
      return;
    }

    setIsSavingRevisionName(true);
    try {
      await renameRevision({
        revisionId: selectedRevision._id,
        revisionName: nextRevisionName,
      });
      toast.success("Revision name updated");
      setIsEditingRevisionName(false);
    } catch (error) {
      console.error(error);
      notifyError(error, error instanceof Error ? error.message : "Failed to rename revision");
    } finally {
      setIsSavingRevisionName(false);
    }
  };

  const {
    isDeletingRevision,
    isDeletingDocument,
    handleDeleteRevision,
    handleDeleteDocument,
  } = useDocumentDeleteActions({
    documentId,
    selectedRevision,
    canDeleteRevision: canDeleteRevisionInUi,
    revisionDeleteBlockedReason: revisionDeleteBlockedReasonInUi,
    canDeleteDocument,
    documentDeleteBlockedReason,
    onDeletedDocument,
  });

  if (previewOnly) {
    return (
      <RevisionPreviewPanel
        sortedRevisions={sortedRevisions}
        selectedRevision={selectedRevision}
        downloadUrl={downloadUrl}
        downloadFilename={downloadFilename}
        onDownload={() => {
          void handleDownload();
        }}
        isDownloading={isDownloading}
        initialPage={previewInitialPage}
        pageLabels={pageLabels}
        className={cn("h-full min-h-0 rounded-none border-0", className, previewClassName)}
      />
    );
  }

  return (
    <div className={cn("space-y-4 overflow-x-hidden", className)}>
      <div className="space-y-2">
        <div className="flex min-w-0 items-center gap-2">
          <Select
            value={selectedRevisionId}
            onValueChange={setSelectedRevisionId}
            disabled={sortedRevisions.length === 0}
          >
            <SelectTrigger className="h-8 min-w-0 flex-1">
              <SelectValue placeholder="Select revision" />
            </SelectTrigger>
            <SelectContent className="max-w-[calc(100vw-4rem)] sm:max-w-[760px]">
              {sortedRevisions.map((revision) => (
                <SelectItem key={revision._id} value={String(revision._id)}>
                  <span className="block max-w-full truncate">
                    {`${revision.revisionName} (r${revision.revisionNumber}) - ${buildDocumentDownloadFilename(
                      documentName,
                      revision.originalFilename,
                      revision.revisionNumber
                    )}`}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <RevisionDeleteButton
            selectedRevision={selectedRevision}
            isUploading={false}
            isDeletingRevision={isDeletingRevision}
            isDeletingDocument={isDeletingDocument}
            disabledReason={revisionDisabledReason}
            onDeleteRevision={() => {
              void handleDeleteRevision();
            }}
          />
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <DocumentActionButtons
            selectedRevision={selectedRevision}
            showViewFullButton={showViewFullButton}
            onViewFull={onViewFull}
            showOpenRevCollabButton={showOpenRevCollabButton}
            onOpenRevCollab={onOpenRevCollab}
            isUploading={false}
            isDeletingRevision={isDeletingRevision}
            isDeletingDocument={isDeletingDocument}
            isCheckingDeleteEligibility={isCheckingDeleteEligibility}
            canDeleteDocument={canDeleteDocument}
            documentDeleteBlockedReason={documentDeleteBlockedReason}
            onDeleteDocument={() => {
              void handleDeleteDocument();
            }}
            isDownloading={isDownloading}
            onDownload={() => {
              void handleDownload();
            }}
            canDownload={Boolean(selectedRevision)}
          />
        </div>
      </div>

      <div className="min-w-0 rounded-lg border bg-muted/20 p-3">
        <RevisionDetailsPanel
          selectedRevision={selectedRevision}
          downloadFilename={downloadFilename}
          revisionDraftName={revisionDraftName}
          isEditingRevisionName={isEditingRevisionName}
          isSavingRevisionName={isSavingRevisionName}
          onRevisionDraftNameChange={setRevisionDraftName}
          onStartRevisionRename={() => {
            setIsEditingRevisionName(true);
          }}
          onCancelRevisionRename={() => {
            setRevisionDraftName(selectedRevision?.revisionName ?? "");
            setIsEditingRevisionName(false);
          }}
          onSaveRevisionName={() => {
            void handleRenameRevision();
          }}
        />
      </div>

      <div
        className={cn(
          "min-h-[480px] rounded-lg border bg-background",
          previewContainerClassName
        )}
      >
        <RevisionPreviewPanel
          sortedRevisions={sortedRevisions}
          selectedRevision={selectedRevision}
          downloadUrl={downloadUrl}
          downloadFilename={downloadFilename}
          initialPage={previewInitialPage}
          pageLabels={pageLabels}
          className={previewClassName}
        />
      </div>
    </div>
  );
}
